import {
  type ChangeEventHandler,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Input } from '@air/primitive-input';

const SEARCH_DEBOUNCE_MS = 300;

export type PDFSearchInputHandle = {
  focus: (selectText: boolean) => void;
  clear: () => void;
  getIsFocused: () => boolean;
  setInitialValue: (value: string) => void;
};

type PDFSearchInputProps = {
  initialValue?: string | null;
  onSearchChange: (value: string) => void;
};

export const PDFSearchInput = memo(
  forwardRef<PDFSearchInputHandle, PDFSearchInputProps>(
    ({ initialValue, onSearchChange }: PDFSearchInputProps, forwardedRef) => {
      const inputRef = useRef<HTMLInputElement>(null);
      const [value, setValue] = useState('');

      useEffect(() => {
        if (initialValue) {
          setValue(initialValue);
        }
      }, [initialValue]);

      useImperativeHandle(
        forwardedRef,
        () => ({
          setInitialValue: (value) => {
            setValue(value);
            onSearchChange(value);
          },
          focus: (selectText: boolean) => {
            inputRef.current?.focus({ preventScroll: true });
            if (selectText) {
              inputRef.current?.select();
            }
          },
          clear: () => {
            setValue('');
            inputRef.current?.blur();
          },
          getIsFocused: () => document.activeElement === inputRef.current,
        }),
        [onSearchChange],
      );

      const [onDebouncedSearch] = useDebouncedCallback(onSearchChange, SEARCH_DEBOUNCE_MS, {
        trailing: true,
      });

      const onValueChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
          const value = event.target.value;
          setValue(value);
          onDebouncedSearch(value);
        },
        [onDebouncedSearch],
      );

      return (
        <Input
          data-testid="PDF_SEARCH_INPUT"
          value={value}
          onChange={onValueChange}
          ref={inputRef}
          className="flex-1 border-none pl-0 text-16 focus-within:border-none focus-within:shadow-none focus:ring-0"
          placeholder="Search document"
        />
      );
    },
  ),
);

PDFSearchInput.displayName = 'PDFSearchInput';
